import AuthEventService from 'shared/services/authEventService'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'

/**
 * Once Harmony turns on, keep it on for the rest of the page session so a transient
 * loading state or refresh never flickers the header back to classic. Reset on logout
 * as a safety net, in case a future change lets login/logout happen without a full
 * page reload (today they always reload, which would reset this anyway).
 */
let harmonyLatched = false
AuthEventService.subscribe((event) => {
    if (event === 'logout') harmonyLatched = false
})

/** Gates the Harmony (light) header: resolves to true when APP_HARMONY_HEADER is enabled for the firm. */
const useIsHarmonyHeader = (): boolean => {
    const { previewFeatures, loading } = usePreviewFeatures()

    // Deliberate render-phase write, not a useEffect: a sibling consumer mounting in the
    // same commit must see the latch immediately, before effects run. Safe because it's a
    // one-way false→true flip, so React 17 strict-mode's double-render can't corrupt it.
    // Revisit if the app moves to React 18 concurrent rendering.
    if (previewFeatures.hasHarmonyHeader) {
        harmonyLatched = true
    }
    if (harmonyLatched) {
        return true
    }

    // Suppress until the flag resolves — avoids a dark-to-Harmony header flash on first paint.
    if (loading) return false
    return previewFeatures.hasHarmonyHeader
}

/** Reset the session latch. Test-only. @internal */
export const resetHarmonyLatchForTests = (): void => {
    if (process.env.NODE_ENV === 'test') {
        harmonyLatched = false
    }
}

export default useIsHarmonyHeader
